import * as React from "react";
import L, { Map as LeafletMap, Layer } from "leaflet";
import "leaflet/dist/leaflet.css";
import { GeoMapperData } from "../types/geo-mapper.types";

type Props = {
  geoData: GeoMapperData;
  dotSize?: number; // default 6
  showLabels?: boolean; // default false
  labelSize?: number; // default 12
  showLegend?: boolean; // default false
  grayscaleTiles?: boolean; // default false
  tileUrl?: string;
  attribution?: string;
  categoryColors?: Record<string, string>;
  style?: React.CSSProperties; // you handle styling
};

const DEFAULT_TILE =
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const DEFAULT_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const PALETTE = [
  "#2563eb", "#16a34a", "#f59e0b", "#ef4444", "#a855f7",
  "#0ea5e9", "#22c55e", "#eab308", "#f97316", "#64748b",
];

export class LeafletVanillaMap extends React.Component<Props> {
  private mapRef = React.createRef<HTMLDivElement>();
  private map?: LeafletMap;
  private tileLayer?: Layer;
  private markers: Layer[] = [];

  componentDidMount() {
    if (!this.mapRef.current) return;

    const {
      tileUrl = DEFAULT_TILE,
      attribution = DEFAULT_ATTR,
      grayscaleTiles = false,
    } = this.props;

    // init map
    this.map = L.map(this.mapRef.current, { zoomControl: false });

    // tile layer
    const tl = L.tileLayer(tileUrl, { maxZoom: 19, attribution });
    this.tileLayer = tl.addTo(this.map);

    // optional grayscale via pane filter
    const pane = (this.map.getPane("tilePane") as HTMLElement) || undefined;
    if (pane) pane.style.filter = grayscaleTiles ? "grayscale(100%)" : "none";

    // add markers & fit bounds
    this.renderPoints();
    this.fitInitialBounds();
    // add a zoom control bottom-right
    L.control.zoom({ position: "bottomright" }).addTo(this.map);
  }

  componentDidUpdate(prev: Props) {
    // update grayscale if changed
    if (this.map && prev.grayscaleTiles !== this.props.grayscaleTiles) {
      const pane = (this.map.getPane("tilePane") as HTMLElement) || undefined;
      if (pane) pane.style.filter = this.props.grayscaleTiles ? "grayscale(100%)" : "none";
    }
    // re-render markers if data/options changed
    if (
      prev.geoData !== this.props.geoData ||
      prev.dotSize !== this.props.dotSize ||
      prev.showLabels !== this.props.showLabels ||
      prev.labelSize !== this.props.labelSize ||
      prev.categoryColors !== this.props.categoryColors
    ) {
      this.clearMarkers();
      this.renderPoints();
      this.fitInitialBounds();
    }
  }

  componentWillUnmount() {
    this.clearMarkers();
    if (this.map) this.map.remove();
  }

  private buildColorMap(): Record<string, string> {
    const { geoData, categoryColors } = this.props;
    const cats = geoData.categories || [];
    if (categoryColors) return categoryColors;
    const map: Record<string, string> = {};
    cats.forEach((c, i) => (map[c] = PALETTE[i % PALETTE.length]));
    return map;
  }

  private renderPoints() {
    if (!this.map) return;
    const { geoData, dotSize = 6, showLabels = false, labelSize = 12 } = this.props;
    const colorMap = this.buildColorMap();

    (geoData.data || []).forEach((p) => {
      const color = (p.category && colorMap[p.category]) || "#3388ff";
      const marker = L.circleMarker([p.latitude, p.longitude], {
        radius: dotSize,
        fillColor: color,
        color: "white",
        weight: 1,
        opacity: 1,
        fillOpacity: 0.85,
      }).addTo(this.map!);

      if (showLabels && p.label) {
        marker.bindTooltip(p.label, {
          permanent: true,
          direction: "top",
          className: "leaflet-tooltip--custom",
          offset: L.point(0, -dotSize),
        });
        // quick inline style for label size; you can move this to CSS
        const node = (marker as any)._tooltip?._container as HTMLElement | undefined;
        if (node) node.style.fontSize = `${labelSize}px`;
      }

      this.markers.push(marker);
    });
  }

  private clearMarkers() {
    this.markers.forEach((m) => m.remove());
    this.markers = [];
  }

  private fitInitialBounds() {
    if (!this.map) return;
    const b = this.props.geoData.bounds;
    if (b) {
      const sw = L.latLng(b.south, b.west);
      const ne = L.latLng(b.north, b.east);
      this.map.fitBounds(L.latLngBounds(sw, ne), { padding: [20, 20] });
    } else if (this.props.geoData.data?.length) {
      const group = L.featureGroup(this.markers as any);
      this.map.fitBounds(group.getBounds(), { padding: [20, 20] });
    } else {
      this.map.setView([0, 0], 2);
    }
  }

  render() {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%", ...(this.props.style || {}) }}>
        <div
          ref={this.mapRef}
          style={{ width: "100%", height: "100%" }}
        />
        {this.props.showLegend && this.props.geoData.categories?.length ? (
          <div
            style={{
              position: "absolute",
              left: 16, bottom: 16, zIndex: 1000,
              padding: 12, background: "rgba(255,255,255,0.9)", borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
              Legend
            </div>
            {(this.props.geoData.categories || []).map((cat) => {
              const color = this.buildColorMap()[cat];
              return (
                <div
                  key={cat}
                  style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
                >
                  <span
                    style={{
                      width: 12, height: 12, borderRadius: "50%", background: color,
                      border: "1px solid rgba(0,0,0,0.1)", marginRight: 8, display: "inline-block",
                    }}
                  />
                  <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    {cat}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }
}
